/**
 * =============================================================================
 * ActivitiesTypeTabs — Abas de filtro por TIPO de atividade com contadores.
 *
 * Visual semelhante ao Feed Atividades: pílulas horizontais com badge numérico.
 * Os contadores vêm da RPC `get_activities_type_counts` e respeitam todos os
 * filtros aplicados na listagem (período, vendedor, workspace, busca, etc.),
 * garantindo coerência total entre abas, KPIs e tabela.
 *
 * @version 1.0.0
 * RELEASE: 2026-05-13 v1.0.0 - Criação inicial
 * =============================================================================
 */
import React from 'react';
import {
  ListChecks,
  Phone,
  Mail,
  MessageSquare,
  Calendar as CalendarIcon,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** Cada aba renderizada. */
interface TabDef {
  key: string;
  label: string;
  icon: LucideIcon;
}

/** Lista canônica de tipos suportados na listagem de Atividades. */
const TABS: TabDef[] = [
  { key: 'all',      label: 'Todos',    icon: Layers },
  { key: 'call',     label: 'Ligação',  icon: Phone },
  { key: 'email',    label: 'E-mail',   icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { key: 'meeting',  label: 'Reunião',  icon: CalendarIcon },
  { key: 'task',     label: 'Lembrete', icon: ListChecks },
];

interface ActivitiesTypeTabsProps {
  /** Tipo selecionado ('all' = sem restrição). */
  value: string;
  /** Callback ao mudar de aba. */
  onChange: (key: string) => void;
  /** Mapa de contadores por tipo (incluindo 'all'). */
  counts: Record<string, number>;
  /** Mostrar skeleton enquanto carrega. */
  isLoading?: boolean;
  className?: string;
}

/** Abas de tipo de atividade com contador (server-side). */
export function ActivitiesTypeTabs({
  value,
  onChange,
  counts,
  isLoading,
  className,
}: ActivitiesTypeTabsProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 px-4 py-2 border-b overflow-x-auto',
        className,
      )}
      role="tablist"
      aria-label="Filtrar por tipo de atividade"
    >
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const active = value === tab.key;
        const count = counts[tab.key] ?? 0;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.key)}
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium whitespace-nowrap transition-colors border',
              active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-transparent hover:bg-muted hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{tab.label}</span>
            <Badge
              variant={active ? 'secondary' : 'outline'}
              className={cn(
                'h-4 px-1.5 text-[10px] leading-none',
                active && 'bg-primary-foreground/20 text-primary-foreground border-transparent',
              )}
            >
              {isLoading ? '…' : count}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}
